// Defaults & Workout freeze fix — pending-modal escape hatch. The action
// modals deliberately block dismissal while a mutation is in flight, but a
// hung request (no fetch timeout exists) used to leave an undismissable
// "Saving…" dialog forever. After an error OR the threshold below, Escape/X
// unlock again. Dismissing does NOT roll the request back — the copy says so.

import { useEffect, useRef, useState } from "react";

/** How long a mutation may stay pending before the modal's Escape/X
 *  unlock anyway. Chosen well under the ~230s Azure gateway timeout so a
 *  hung request never locks the page for minutes. */
export const MODAL_PENDING_ESCAPE_MS = 30_000;

export const PENDING_DISMISS_NOTICE =
  "The request may still complete — refresh to see the result.";

export function usePendingEscapeHatch(isPending: boolean, hasError: boolean): {
  /** Whether onClose may proceed right now. */
  canDismiss: boolean;
  /** Non-null once a still-pending dialog is dismissible — render it so the
   *  user knows closing isn't a rollback. */
  pendingNotice: string | null;
} {
  const [thresholdPassed, setThresholdPassed] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!isPending) {
      setThresholdPassed(false);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
      return;
    }
    timer.current = window.setTimeout(
      () => setThresholdPassed(true),
      MODAL_PENDING_ESCAPE_MS,
    );
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    };
  }, [isPending]);

  const canDismiss = !isPending || hasError || thresholdPassed;
  return {
    canDismiss,
    pendingNotice: isPending && canDismiss ? PENDING_DISMISS_NOTICE : null,
  };
}
